import type {
  DebriefAnalysis,
  DebriefCrmFact,
  DebriefEvidenceStatus,
  DebriefNextActionRecommendation,
  DebriefObjection,
  DebriefObjectionCategory,
  DebriefPlaybookSignal,
  DebriefShotScoreRecommendation
} from "./types";
import type { NextStepDraft } from "./uiTypes";

export const DEBRIEF_ERROR_CATEGORIES = [
  "INVOCATION_ERROR",
  "MISSING_CONFIGURATION",
  "OPENAI_AUTH_ERROR",
  "OPENAI_MODEL_ERROR",
  "OPENAI_PERMISSION_ERROR",
  "OPENAI_QUOTA_ERROR",
  "OPENAI_RATE_LIMIT",
  "OPENAI_INVALID_REQUEST",
  "OPENAI_TIMEOUT",
  "OPENAI_NETWORK_ERROR",
  "OPENAI_RESPONSE_ERROR",
  "TRANSCRIPTION_ERROR",
  "JSON_PARSE_ERROR",
  "SCHEMA_VALIDATION_ERROR",
  "UNEXPECTED_ERROR"
] as const;

export type DebriefErrorCategory = (typeof DEBRIEF_ERROR_CATEGORIES)[number];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
const strings = (value: unknown) => Array.isArray(value)
  ? value.map(text).filter(Boolean).slice(0, 12)
  : [];
const evidenceStatus = (value: unknown): DebriefEvidenceStatus =>
  value === "confirmed" || value === "missed" || value === "unclear" ? value : "unclear";

const shotScoreDimension = (value: unknown): DebriefShotScoreRecommendation | undefined => {
  if (!isRecord(value) || typeof value.value !== "boolean") return undefined;
  const evidence = text(value.evidence);
  if (!evidence) return undefined;
  return {
    value: value.value,
    evidence,
    status: evidenceStatus(value.status),
    explanation: text(value.explanation) || undefined
  };
};

const objectionCategories = new Set<DebriefObjectionCategory>([
  "SELF_SALE", "NO_AGENCY", "BAD_AGENT_EXPERIENCE", "COMMISSION", "NET_PROCEEDS",
  "LEGAL_SERVICES", "MULTIPLE_AGENCIES", "UNREALISTIC_PRICE", "FINANCING", "DECISION_MAKER", "OTHER"
]);

const objections = (value: unknown): DebriefObjection[] => !Array.isArray(value) ? [] : value.flatMap((item) => {
  if (!isRecord(item)) return [];
  const category = objectionCategories.has(item.category as DebriefObjectionCategory)
    ? item.category as DebriefObjectionCategory
    : "OTHER";
  const wording = text(item.wording);
  const evidence = text(item.evidence);
  return wording && evidence ? [{ category, wording, evidence }] : [];
}).slice(0, 8);

const crmFactKinds = new Set<DebriefCrmFact["kind"]>([
  "motivation", "timeline", "price_expectation", "decision_maker", "agency", "self_sale",
  "financing", "property_condition", "concern", "other"
]);

const crmFacts = (value: unknown): DebriefCrmFact[] => !Array.isArray(value) ? [] : value.flatMap((item, index) => {
  if (!isRecord(item)) return [];
  const kind = crmFactKinds.has(item.kind as DebriefCrmFact["kind"]) ? item.kind as DebriefCrmFact["kind"] : "other";
  const label = text(item.label);
  const factValue = text(item.value);
  const evidence = text(item.evidence);
  if (!label || !factValue || !evidence) return [];
  return [{ id: text(item.id) || `fact-${index + 1}`, kind, label, value: factValue, evidence }];
}).slice(0, 12);

const playbookSignals = (value: unknown): DebriefPlaybookSignal[] => !Array.isArray(value) ? [] : value.flatMap((item) => {
  if (!isRecord(item)) return [];
  const key = text(item.key);
  const label = text(item.label);
  if (!key || !label) return [];
  return [{ key, label, status: evidenceStatus(item.status), evidence: text(item.evidence) || "Z debriefu sa nedá potvrdiť." }];
}).slice(0, 16);

const nextAction = (value: unknown): DebriefNextActionRecommendation | undefined => {
  if (!isRecord(value)) return undefined;
  const purpose = text(value.purpose);
  if (!purpose) return undefined;
  const confidence = value.confidence === "high" || value.confidence === "medium" || value.confidence === "low"
    ? value.confidence
    : "low";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text(value.date)) ? text(value.date) : undefined;
  const time = /^([01]\d|2[0-3]):[0-5]\d$/.test(text(value.time)) ? text(value.time) : undefined;
  return { type: text(value.type) || "Follow-up", date, time, purpose, evidence: text(value.evidence), confidence };
};

export const parseDebriefAnalysis = (value: unknown): DebriefAnalysis => {
  if (!isRecord(value)) throw new Error("AI odpoveď nemá platný formát.");
  const summary = text(value.summary);
  const nextStep = text(value.nextStep);
  if (!summary || !nextStep) throw new Error("AI odpoveď neobsahuje povinné zhrnutie a ďalší krok.");
  const recommendation = isRecord(value.shotScoreRecommendation) ? value.shotScoreRecommendation : undefined;
  const identity = shotScoreDimension(recommendation?.identity);
  const valueDimension = shotScoreDimension(recommendation?.value);
  const acquisition = shotScoreDimension(recommendation?.acquisition);
  const nextStepDimension = shotScoreDimension(recommendation?.nextStep);
  if (recommendation && (!identity || !valueDimension || !acquisition || !nextStepDimension)) {
    throw new Error("AI odpoveď obsahuje neplatné Shot Score odporúčanie.");
  }
  return {
    summary,
    positives: strings(value.positives),
    missed: strings(value.missed),
    nextStep,
    coaching: text(value.coaching),
    risks: strings(value.risks),
    objections: objections(value.objections),
    crmFacts: crmFacts(value.crmFacts),
    playbookSignals: playbookSignals(value.playbookSignals),
    nextActionRecommendation: nextAction(value.nextActionRecommendation),
    shotScoreRecommendation: identity && valueDimension && acquisition && nextStepDimension
      ? { identity, value: valueDimension, acquisition, nextStep: nextStepDimension }
      : undefined
  };
};

const localDate = (value: Date) => [
  value.getFullYear(),
  String(value.getMonth() + 1).padStart(2, "0"),
  String(value.getDate()).padStart(2, "0")
].join("-");

export const nextStepDraftFromAnalysis = (analysis: DebriefAnalysis, now = new Date()): NextStepDraft => {
  const recommendation = analysis.nextActionRecommendation;
  const fallback = new Date(now.getTime() + 24 * 60 * 60_000);
  const recommendedAt = recommendation?.date
    ? new Date(`${recommendation.date}T${recommendation.time ?? "09:00"}:00`)
    : undefined;
  const validFuture = recommendedAt && Number.isFinite(recommendedAt.getTime()) && recommendedAt > now;
  return {
    date: validFuture ? recommendation!.date! : localDate(fallback),
    time: validFuture ? recommendation?.time ?? "09:00" : "09:00",
    purpose: recommendation?.purpose || analysis.nextStep
  };
};
